import { useFieldContext } from '@ark-ui/solid';
import { Checkbox as ACheckbox } from '@ark-ui/solid/checkbox';
import { createUniqueId, useContext, createContext, splitProps, Show } from 'solid-js';
import { Field } from './field.tsx';
import { FormContext } from './form.tsx';
import type { ComponentProps, JSXElement } from 'solid-js';

export function Checkbox(props: ComponentProps<typeof ACheckbox.Root>) {
	const { data } = useContext(FormContext);

	return (
		<Field name={props.name ?? ''}>
			<ACheckbox.Root {...props} class="group/check relative flex items-start gap-x-2.5">
				<ACheckbox.HiddenInput
					disabled={data().pending || props.readOnly || props.disabled}
					tabindex={props.tabindex ?? 0}
				/>
				<ACheckbox.Control class="relative size-6 shrink-0 appearance-none rounded-sm border border-zinc-300 bg-white transition-all duration-200 outline-none group-hover/check:bg-yellow-500/50 group-has-focus-visible/check:ring-4 group-has-focus-visible/check:ring-yellow-500/50 group-has-focus-visible/check:ring-offset-1 group-has-focus-visible/check:ring-offset-zinc-50 aria-invalid:border-rose-600 data-readonly:border-zinc-400 data-readonly:bg-zinc-200 data-[state=checked]:border-yellow-500 data-[state=checked]:bg-yellow-500 data-readonly:data-[state=checked]:border-zinc-400 data-readonly:data-[state=checked]:bg-zinc-400 motion-safe:data-readonly:group-has-active/check:animate-shake dark:border-zinc-700 dark:bg-black dark:group-has-focus-visible/check:ring-offset-zinc-950 dark:data-readonly:border-zinc-700 dark:data-readonly:bg-zinc-800 data-active:[&>svg]:scale-90 data-active:[&>svg]:stroke-zinc-50 data-[state=checked]:[&>svg]:scale-100 data-[state=checked]:[&>svg]:stroke-white dark:data-[state=checked]:[&>svg]:stroke-black">
					<ACheckbox.Indicator
						asChild={(props) => {
							const { hidden, ...rest } = props();
							return (
								<svg
									{...rest}
									class="pointer-events-none absolute inset-0 m-auto block size-5 scale-50 stroke-transparent transition-all duration-200"
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									fill="none"
									stroke-width="4"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<polyline points="20 6 9 17 4 12" />
								</svg>
							);
						}}
					/>
				</ACheckbox.Control>
				<div class="flex shrink flex-col gap-0.5">{props.children}</div>
			</ACheckbox.Root>
		</Field>
	);
}

Checkbox.Label = (props: ComponentProps<typeof ACheckbox.Label>) => {
	return (
		<ACheckbox.Label {...props} class="text-base font-medium">
			{props.children}
			<Field.Required />
		</ACheckbox.Label>
	);
};

const Group = createContext<string>('');

// We're explicitly avoiding using Checkbox.Fieldset because fieldsets are actually crap for accessibility
Checkbox.Group = (props: ComponentProps<typeof ACheckbox.Group>) => {
	return (
		<Field name={props.name ?? ''}>
			<GroupInternal {...props} />
		</Field>
	);
};

function GroupInternal(props: ComponentProps<typeof ACheckbox.Group>) {
	const id = createUniqueId();
	const ctx = useFieldContext();
	return (
		<Group.Provider value={id}>
			<ACheckbox.Group
				{...props}
				class="flex flex-col gap-y-3"
				aria-labelledby={`checkboxgroup:${id}`}
				aria-describedby={ctx().ariaDescribedby}
				role="group"
			/>
		</Group.Provider>
	);
}

Checkbox.GroupLabel = (props: ComponentProps<typeof Checkbox.Label>) => {
	const group = useContext(Group);
	return (
		<label class="text-lg font-medium" id={`checkboxgroup:${group}`}>
			{props.children}
		</label>
	);
};

export function CheckboxField(
	props: ComponentProps<typeof Checkbox> & { name?: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	return (
		<Checkbox {...rest}>
			<Checkbox.Label>{other.label}</Checkbox.Label>
			<Field.Errors />
			<Show when={other.description}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</Checkbox>
	);
}

export function CheckboxGroupField(
	props: ComponentProps<typeof Checkbox.Group> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	return (
		<Checkbox.Group {...rest}>
			<Checkbox.GroupLabel>{other.label}</Checkbox.GroupLabel>
			{props.children}
			<Field.Errors />
			<Show when={other.description}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</Checkbox.Group>
	);
}
